/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\SettingsTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the openvpn module.
----------------------------------------------------------------*/


import React from 'react';
import { Server, Settings } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { FieldWithReset } from '../../httpServer/components/FieldWithReset';
import { DEFAULT_OPENVPN_CONFIG } from '../types';
import type { OpenVPNConfigFormState, OpenVPNProtocol, OpenVPNDeviceType, OpenVPNCompression } from '../types';

interface SettingsTabProps {
    formData: OpenVPNConfigFormState;
    loading: boolean;
    updateField: <K extends keyof OpenVPNConfigFormState>(field: K, value: OpenVPNConfigFormState[K]) => void;
}

export function SettingsTab({ formData, loading, updateField }: SettingsTabProps) {
    const { t } = useI18n();
    const resetTooltip = t('openvpn.tooltips.resetField', 'Reset to default');
    const selectClasses = 'w-full px-3 py-2 text-sm border rounded bg-background text-foreground border-border focus:outline-none focus:ring-1 focus:ring-primary';

    if (loading) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Server Settings */}
            <FormBox
                title={t('openvpn.sections.server', 'Server Settings')}
                description={t('openvpn.sections.serverDesc', 'Basic server configuration')}
                icon={<Server className="h-5 w-5" />}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    {/* Row 1: Enabled | Server Address */}
                    <SimpleTooltip
                        content={t('openvpn.tooltips.enabled', 'Enable or disable the OpenVPN server')}
                        title={t('openvpn.fields.enabled', 'Enabled')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="enabled" className="text-left">
                            {t('openvpn.fields.enabled', 'Enabled')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.enabled}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.enabled}
                        onReset={() => updateField('enabled', DEFAULT_OPENVPN_CONFIG.enabled)}
                        resetTooltip={resetTooltip}
                    >
                        <Toggle
                            value={formData.enabled}
                            onChange={(value) => updateField('enabled', value)}
                        />
                    </FieldWithReset>

                    <SimpleTooltip
                        content={t('openvpn.tooltips.publicHost', 'Public IP address or hostname that VPN clients will connect to (required)')}
                        title={t('openvpn.fields.publicHost', 'Public Host')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="serverAddress" className="text-left">
                            {t('openvpn.fields.publicHost', 'Public Host')} <span className="text-destructive">*</span>
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.serverAddress}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.serverAddress}
                        onReset={() => updateField('serverAddress', DEFAULT_OPENVPN_CONFIG.serverAddress)}
                        resetTooltip={resetTooltip}
                    >
                        <Input
                            id="serverAddress"
                            value={formData.serverAddress}
                            onChange={(e) => updateField('serverAddress', e.target.value)}
                            placeholder="vpn.example.com"
                            required
                        />
                    </FieldWithReset>

                    {/* Row 2: Server Port | Protocol */}
                    <SimpleTooltip
                        content={t('openvpn.tooltips.serverPort', 'Port for OpenVPN connections (default: 1194)')}
                        title={t('openvpn.fields.serverPort', 'Server Port')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="serverPort" className="text-left">
                            {t('openvpn.fields.serverPort', 'Server Port')} <span className="text-destructive">*</span>
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.serverPort}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.serverPort}
                        onReset={() => updateField('serverPort', DEFAULT_OPENVPN_CONFIG.serverPort)}
                        resetTooltip={resetTooltip}
                    >
                        <Input
                            id="serverPort"
                            type="number"
                            value={formData.serverPort}
                            onChange={(e) => updateField('serverPort', parseInt(e.target.value) || 1194)}
                            min={1}
                            max={65535}
                            required
                        />
                    </FieldWithReset>

                    <SimpleTooltip
                        content={t('openvpn.tooltips.protocol', 'Connection protocol (UDP recommended for performance)')}
                        title={t('openvpn.fields.protocol', 'Protocol')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="protocol" className="text-left">
                            {t('openvpn.fields.protocol', 'Protocol')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.protocol}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.protocol}
                        onReset={() => updateField('protocol', DEFAULT_OPENVPN_CONFIG.protocol)}
                        resetTooltip={resetTooltip}
                    >
                        <select
                            id="protocol"
                            value={formData.protocol}
                            onChange={(e) => updateField('protocol', e.target.value as OpenVPNProtocol)}
                            className={selectClasses}
                        >
                            <option value="udp">UDP</option>
                            <option value="tcp">TCP</option>
                        </select>
                    </FieldWithReset>

                    {/* Row 3: Device Type | Compression */}
                    <SimpleTooltip
                        content={t('openvpn.tooltips.deviceType', 'TUN for routing, TAP for bridging')}
                        title={t('openvpn.fields.deviceType', 'Device Type')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="deviceType" className="text-left">
                            {t('openvpn.fields.deviceType', 'Device Type')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.deviceType}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.deviceType}
                        onReset={() => updateField('deviceType', DEFAULT_OPENVPN_CONFIG.deviceType)}
                        resetTooltip={resetTooltip}
                    >
                        <select
                            id="deviceType"
                            value={formData.deviceType}
                            onChange={(e) => updateField('deviceType', e.target.value as OpenVPNDeviceType)}
                            className={selectClasses}
                        >
                            <option value="tun">TUN (Routing)</option>
                            <option value="tap">TAP (Bridging)</option>
                        </select>
                    </FieldWithReset>

                    <SimpleTooltip
                        content={t('openvpn.tooltips.compress', 'Compression algorithm for VPN traffic')}
                        title={t('openvpn.fields.compress', 'Compression')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="compress" className="text-left">
                            {t('openvpn.fields.compress', 'Compression')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.compress}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.compress}
                        onReset={() => updateField('compress', DEFAULT_OPENVPN_CONFIG.compress)}
                        resetTooltip={resetTooltip}
                    >
                        <select
                            id="compress"
                            value={formData.compress}
                            onChange={(e) => updateField('compress', e.target.value as OpenVPNCompression)}
                            className={selectClasses}
                        >
                            <option value="no">{t('openvpn.compression.no', 'Disabled (Recommended)')}</option>
                            <option value="lz4-v2">{t('openvpn.compression.lz4v2', 'LZ4-v2 (Fast)')}</option>
                            <option value="lz4">{t('openvpn.compression.lz4', 'LZ4')}</option>
                            <option value="lzo">{t('openvpn.compression.lzo', 'LZO (Legacy)')}</option>
                        </select>
                    </FieldWithReset>

                    {/* Row 4: VPN Subnet | DNS Servers */}
                    <SimpleTooltip
                        content={t('openvpn.tooltips.vpnSubnet', 'IP address range and subnet mask for VPN clients')}
                        title={t('openvpn.fields.vpnSubnet', 'VPN Subnet')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="vpnSubnet" className="text-left">
                            {t('openvpn.fields.vpnSubnet', 'VPN Subnet')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={`${formData.vpnSubnet}/${formData.vpnNetmask}`}
                        defaultValue={`${DEFAULT_OPENVPN_CONFIG.vpnSubnet}/${DEFAULT_OPENVPN_CONFIG.vpnNetmask}`}
                        onReset={() => {
                            updateField('vpnSubnet', DEFAULT_OPENVPN_CONFIG.vpnSubnet);
                            updateField('vpnNetmask', DEFAULT_OPENVPN_CONFIG.vpnNetmask);
                        }}
                        resetTooltip={resetTooltip}
                    >
                        <div className="flex items-center">
                            <Input
                                id="vpnSubnet"
                                value={formData.vpnSubnet}
                                onChange={(e) => updateField('vpnSubnet', e.target.value)}
                                placeholder="10.8.0.0"
                                className="rounded-r-none border-r-0"
                            />
                            <span className="px-3 py-2 bg-muted border border-border text-sm text-muted-foreground">/</span>
                            <Input
                                id="vpnNetmask"
                                value={formData.vpnNetmask}
                                onChange={(e) => updateField('vpnNetmask', e.target.value)}
                                placeholder="255.255.255.0"
                                className="rounded-l-none border-l-0"
                            />
                        </div>
                    </FieldWithReset>

                    <SimpleTooltip
                        content={t('openvpn.tooltips.dnsServers', 'Primary and secondary DNS servers for VPN clients')}
                        title={t('openvpn.fields.dnsServers', 'DNS Servers')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="dnsServer1" className="text-left">
                            {t('openvpn.fields.dnsServers', 'DNS Servers')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={`${formData.dnsServer1}/${formData.dnsServer2}`}
                        defaultValue={`${DEFAULT_OPENVPN_CONFIG.dnsServer1}/${DEFAULT_OPENVPN_CONFIG.dnsServer2}`}
                        onReset={() => {
                            updateField('dnsServer1', DEFAULT_OPENVPN_CONFIG.dnsServer1);
                            updateField('dnsServer2', DEFAULT_OPENVPN_CONFIG.dnsServer2);
                        }}
                        resetTooltip={resetTooltip}
                    >
                        <div className="flex items-center">
                            <Input
                                id="dnsServer1"
                                value={formData.dnsServer1}
                                onChange={(e) => updateField('dnsServer1', e.target.value)}
                                placeholder="8.8.8.8"
                                className="rounded-r-none border-r-0"
                            />
                            <span className="px-3 py-2 bg-muted border border-border text-sm text-muted-foreground">/</span>
                            <Input
                                id="dnsServer2"
                                value={formData.dnsServer2}
                                onChange={(e) => updateField('dnsServer2', e.target.value)}
                                placeholder="8.8.4.4"
                                className="rounded-l-none border-l-0"
                            />
                        </div>
                    </FieldWithReset>
                </div>
            </FormBox>

            {/* Advanced Settings */}
            <FormBox
                title={t('openvpn.sections.advanced', 'Advanced Settings')}
                description={t('openvpn.sections.advancedDesc', 'Security, encryption and performance')}
                icon={<Settings className="h-5 w-5" />}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    {/* Row 1: Cipher | Auth */}
                    <SimpleTooltip
                        content={t('openvpn.tooltips.cipher', 'Encryption algorithm for data channel')}
                        title={t('openvpn.fields.cipher', 'Cipher')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="cipher" className="text-left">
                            {t('openvpn.fields.cipher', 'Cipher')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.cipher}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.cipher}
                        onReset={() => updateField('cipher', DEFAULT_OPENVPN_CONFIG.cipher)}
                        resetTooltip={resetTooltip}
                    >
                        <select
                            id="cipher"
                            value={formData.cipher}
                            onChange={(e) => updateField('cipher', e.target.value)}
                            className={selectClasses}
                        >
                            <option value="AES-256-GCM">AES-256-GCM (Recommended)</option>
                            <option value="AES-128-GCM">AES-128-GCM</option>
                            <option value="AES-256-CBC">AES-256-CBC</option>
                            <option value="AES-128-CBC">AES-128-CBC</option>
                        </select>
                    </FieldWithReset>

                    <SimpleTooltip
                        content={t('openvpn.tooltips.auth', 'HMAC authentication algorithm')}
                        title={t('openvpn.fields.auth', 'Auth Algorithm')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="auth" className="text-left">
                            {t('openvpn.fields.auth', 'Auth Algorithm')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.auth}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.auth}
                        onReset={() => updateField('auth', DEFAULT_OPENVPN_CONFIG.auth)}
                        resetTooltip={resetTooltip}
                    >
                        <select
                            id="auth"
                            value={formData.auth}
                            onChange={(e) => updateField('auth', e.target.value)}
                            className={selectClasses}
                        >
                            <option value="SHA256">SHA256 (Recommended)</option>
                            <option value="SHA384">SHA384</option>
                            <option value="SHA512">SHA512</option>
                            <option value="SHA1">SHA1 (Legacy)</option>
                        </select>
                    </FieldWithReset>

                    {/* Row 2: TLS Min Version | Max Clients */}
                    <SimpleTooltip
                        content={t('openvpn.tooltips.tlsVersionMin', 'Minimum TLS version for control channel')}
                        title={t('openvpn.fields.tlsVersionMin', 'TLS Min Version')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="tlsVersionMin" className="text-left">
                            {t('openvpn.fields.tlsVersionMin', 'TLS Min Version')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.tlsVersionMin}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.tlsVersionMin}
                        onReset={() => updateField('tlsVersionMin', DEFAULT_OPENVPN_CONFIG.tlsVersionMin)}
                        resetTooltip={resetTooltip}
                    >
                        <select
                            id="tlsVersionMin"
                            value={formData.tlsVersionMin}
                            onChange={(e) => updateField('tlsVersionMin', e.target.value)}
                            className={selectClasses}
                        >
                            <option value="1.0">TLS 1.0</option>
                            <option value="1.1">TLS 1.1</option>
                            <option value="1.2">TLS 1.2 (Recommended)</option>
                            <option value="1.3">TLS 1.3</option>
                        </select>
                    </FieldWithReset>

                    <SimpleTooltip
                        content={t('openvpn.tooltips.maxClients', 'Maximum concurrent client connections')}
                        title={t('openvpn.fields.maxClients', 'Max Clients')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="maxClients" className="text-left">
                            {t('openvpn.fields.maxClients', 'Max Clients')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.maxClients}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.maxClients}
                        onReset={() => updateField('maxClients', DEFAULT_OPENVPN_CONFIG.maxClients)}
                        resetTooltip={resetTooltip}
                    >
                        <Input
                            id="maxClients"
                            type="number"
                            value={formData.maxClients}
                            onChange={(e) => updateField('maxClients', parseInt(e.target.value) || 100)}
                            min={1}
                        />
                    </FieldWithReset>

                    {/* Row 3: Keepalive Interval | Keepalive Timeout */}
                    <SimpleTooltip
                        content={t('openvpn.tooltips.keepaliveInterval', 'Seconds between keepalive pings')}
                        title={t('openvpn.fields.keepaliveInterval', 'Keepalive Interval')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="keepaliveInterval" className="text-left">
                            {t('openvpn.fields.keepaliveInterval', 'Keepalive Interval')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.keepaliveInterval}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.keepaliveInterval}
                        onReset={() => updateField('keepaliveInterval', DEFAULT_OPENVPN_CONFIG.keepaliveInterval)}
                        resetTooltip={resetTooltip}
                    >
                        <Input
                            id="keepaliveInterval"
                            type="number"
                            value={formData.keepaliveInterval}
                            onChange={(e) => updateField('keepaliveInterval', parseInt(e.target.value) || 10)}
                            min={1}
                        />
                    </FieldWithReset>

                    <SimpleTooltip
                        content={t('openvpn.tooltips.keepaliveTimeout', 'Seconds before considering connection dead')}
                        title={t('openvpn.fields.keepaliveTimeout', 'Keepalive Timeout')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="keepaliveTimeout" className="text-left">
                            {t('openvpn.fields.keepaliveTimeout', 'Keepalive Timeout')}
                        </Label>
                    </SimpleTooltip>
                    <FieldWithReset
                        currentValue={formData.keepaliveTimeout}
                        defaultValue={DEFAULT_OPENVPN_CONFIG.keepaliveTimeout}
                        onReset={() => updateField('keepaliveTimeout', DEFAULT_OPENVPN_CONFIG.keepaliveTimeout)}
                        resetTooltip={resetTooltip}
                    >
                        <Input
                            id="keepaliveTimeout"
                            type="number"
                            value={formData.keepaliveTimeout}
                            onChange={(e) => updateField('keepaliveTimeout', parseInt(e.target.value) || 120)}
                            min={1}
                        />
                    </FieldWithReset>
                </div>
            </FormBox>
        </div>
    );
}
